/** What a failure screen hands to the support form: the resource it was about, the operation's own
 * reference when there is one, and a short plain-language reason — never the provider's reference,
 * failure codes or raw error detail. Built only from fields already shown to the customer, so there is
 * nothing here for {@link redactSupportContext} to find, short of a bug upstream — which is exactly
 * why it still runs. */
export interface SupportContext {
  resourceType: string;
  resourceId: string;
  reference?: string;
  reason: string;
}

const MAX_PART_LENGTH = 120;

/** Shapes that must never leave the browser in a support request, however they got into a context
 * value: long opaque tokens, hex/base64 blobs (hashes, keys), and e-mail addresses (never part of an
 * auto-built reference line). */
const SENSITIVE_PATTERNS: RegExp[] = [
  /[A-Za-z0-9+/]{32,}={0,2}/,
  /[0-9a-fA-F]{32,}/,
  /\bsk-[A-Za-z0-9_-]{10,}/i,
  /[\w.+-]+@[\w-]+\.[\w.-]+/,
];

function sanitizePart(value: string): string {
  const trimmed = value.trim().slice(0, MAX_PART_LENGTH);
  return SENSITIVE_PATTERNS.some((pattern) => pattern.test(trimmed)) ? "[redacted]" : trimmed;
}

export function redactSupportContext(context: SupportContext): SupportContext {
  return {
    resourceType: sanitizePart(context.resourceType),
    resourceId: sanitizePart(context.resourceId),
    reference: context.reference ? sanitizePart(context.reference) : undefined,
    reason: sanitizePart(context.reason),
  };
}

/** The support form's Subject line for a request opened from a failure screen: always carries what the
 * customer was doing and its reference, so the agent never has to ask for it first. */
export function supportContextTitle(context: SupportContext): string {
  const safe = redactSupportContext(context);
  const ref = safe.reference ? ` (ref ${safe.reference})` : "";
  return `${safe.reason} — ${safe.resourceType} ${safe.resourceId}${ref}`;
}
